import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import { doc, getDoc, collection, addDoc, serverTimestamp, query, where, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../index';
import { Player, GameSession, GameState, Submission } from '../types';
import { usePlayer, useGameSession } from '../hooks/useGameSession';
import { checkAnswer, normalizeJapanese } from '../utils/normalize';
import { updateSessionTeamScores } from '../utils/teamScore';
import { TEAM_COLORS } from '../constants/teamColors';
import { BATTLE_ROYALE_ANSWERS } from '../data/answers';

interface FeedbackState {
  open: boolean;
  type: 'correct' | 'incorrect' | 'duplicate';
  message: string;
}

export const GameScreen: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>({
    open: false,
    type: 'correct',
    message: ''
  });
  const [lastSubmission, setLastSubmission] = useState<string>('');

  const playerId = sessionStorage.getItem('battleRoyalePlayerId');
  const { player } = usePlayer(sessionId || '', playerId || '');
  const { session } = useGameSession(sessionId || '');

  // 互換性のため、answeredIdsが存在しない場合はscoreを使用
  const displayScore = player?.answeredIds ? player.answeredIds.length : (player?.score || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sessionId || !playerId || !player || !session) {
      return;
    }

    if (!answer.trim()) {
      return;
    }

    if (session.state !== GameState.PLAYING) {
      setFeedback({
        open: true,
        type: 'incorrect',
        message: 'ゲームは終了しました'
      });
      return;
    }

    setLoading(true);
    try {
      const normalizedInput = normalizeJapanese(answer);
      
      // 正解チェック
      let correctAnswer = null;
      let isCorrect = false;
      
      for (const ans of session.answers) {
        if (checkAnswer(answer, ans.canonical, ans.synonyms)) {
          correctAnswer = ans;
          isCorrect = true;
          break;
        }
      }

      // 自分のOP答えかチェック
      if (isCorrect && correctAnswer && player.ownAnswerId === correctAnswer.id) {
        setFeedback({
          open: true,
          type: 'incorrect',
          message: '自分の謎の答えです'
        });
        setAnswer('');
        setLoading(false);
        return;
      }

      // 重複チェック
      if (isCorrect && correctAnswer) {
        const submissionsQuery = query(
          collection(db, 'battleRoyale', sessionId, 'submissions'),
          where('playerId', '==', playerId),
          where('answerId', '==', correctAnswer.id)
        );
        const existingSubmissions = await getDocs(submissionsQuery);
        
        if (!existingSubmissions.empty) {
          setFeedback({
            open: true,
            type: 'duplicate',
            message: '既出'
          });
          setLastSubmission(answer);
          setAnswer('');
          setLoading(false);
          return;
        }
      }

      // 提出を記録
      const submissionData: Omit<Submission, 'id'> = {
        playerId,
        answerId: correctAnswer?.id || '',
        answerText: answer,
        isCorrect,
        points: isCorrect ? (correctAnswer?.points || 1) : 0,
        submittedAt: new Date(),
        isDuplicate: false
      };

      await addDoc(
        collection(db, 'battleRoyale', sessionId, 'submissions'),
        {
          ...submissionData,
          submittedAt: serverTimestamp()
        }
      );

      // スコア更新
      if (isCorrect && correctAnswer) {
        // answeredIds配列に追加（重複しないようにarrayUnionを使用）
        await updateDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId), {
          answeredIds: arrayUnion(correctAnswer.id),
          lastCorrectAt: serverTimestamp()
        });
        
        // 更新後のプレイヤー情報を取得してスコアを更新
        const updatedPlayerDoc = await getDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId));
        if (updatedPlayerDoc.exists()) {
          const updatedPlayer = updatedPlayerDoc.data() as Player;
          const newScore = updatedPlayer.answeredIds?.length || 0;
          
          await updateDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId), {
            score: newScore
          });
        }
        
        // チームスコアを更新
        await updateSessionTeamScores(sessionId);
      }

      // フィードバック表示
      setFeedback({
        open: true,
        type: isCorrect ? 'correct' : 'incorrect',
        message: isCorrect ? '正解' : '不正解'
      });
      setLastSubmission(answer);
      setAnswer('');

    } catch (err) {
      console.error('Submission error:', err);
      setFeedback({
        open: true,
        type: 'incorrect',
        message: 'エラーが発生しました'
      });
    } finally {
      setLoading(false);
    }
  };


  const teamColor = player?.teamColor ? TEAM_COLORS[player.teamColor].colorCode : '#808080';
  
  // プレイヤーの謎の答えを取得
  const playerAnswer = player?.ownAnswerId ? 
    BATTLE_ROYALE_ANSWERS.find(ans => ans.id === player.ownAnswerId) : null;
  const playerAnswerText = playerAnswer?.canonical || '---';
  const answerSubColor = playerAnswer?.subColor || '#f0f0f0';

  return (
    <Container maxWidth="sm" sx={{ px: 0 }}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          bgcolor: 'white'
        }}
      >
        {/* ヘッダー - チームカラーバー */}
        <Box sx={{ 
          bgcolor: teamColor, 
          color: 'white', 
          py: 2, 
          px: 3
        }}>
          <Typography variant="h6" align="center">
            あなたのテーブル番号 {player?.tableNumber || '-'}
          </Typography>
        </Box>

        {/* メインコンテンツ */}
        <Box sx={{ flex: 1, py: 3, px: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            あなたの謎の答え
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {playerAnswerText}
          </Typography>
        </Box>

        <Box sx={{ 
          mb: 4,
          mx: -3,  // コンテナのパディングを相殺
          px: 3,   // 内側のパディングを追加
          py: 2,
          bgcolor: answerSubColor
        }}>
          <Typography variant="body2" color="text.secondary">
            あなたの獲得ポイント
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
            {displayScore}
          </Typography>
        </Box>

        <Box>
          {session?.state === GameState.ENDED && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h5" 
                sx={{ fontWeight: 'bold', mb: 2 }}
              >
                ゲームが終了しました
              </Typography>
              <Typography variant="body1">
                結果発表をお待ちください
              </Typography>
            </Box>
          )}
          {feedback.open && feedback.type === 'correct' && session?.state !== GameState.ENDED && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h5" 
                sx={{ color: 'red', fontWeight: 'bold' }}
              >
                正解！
              </Typography>
            </Box>
          )}
          {feedback.open && feedback.type === 'incorrect' && session?.state !== GameState.ENDED && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h5" 
                sx={{ color: 'blue', fontWeight: 'bold' }}
              >
                不正解！
              </Typography>
            </Box>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="答えを入力"
              variant="outlined"
              disabled={loading || session?.state !== GameState.PLAYING}
              sx={{
                mb: 2,
                bgcolor: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !answer.trim() || session?.state !== GameState.PLAYING}
              sx={{
                py: 2,
                bgcolor: session?.state === GameState.WAITING ? 'grey.600' : 'grey.800',
                '&:hover': {
                  bgcolor: session?.state === GameState.WAITING ? 'grey.600' : 'grey.900'
                }
              }}
            >
              {session?.state === GameState.WAITING ? '開催をお待ちください' : 
               session?.state === GameState.ENDED ? '送信' : '送信'}
            </Button>
          </form>
        </Box>
      </Box>

        {/* フッター - チームカラーバー */}
        <Box sx={{ 
          bgcolor: teamColor, 
          color: 'white', 
          py: 2, 
          px: 3
        }}>
          {/* 背景のみ */}
        </Box>

        <Snackbar
          open={feedback.open}
          autoHideDuration={2000}
          onClose={() => setFeedback({ ...feedback, open: false })}
        />
      </Box>
    </Container>
  );
};